import { Stack, Switch } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { SettingRow } from "@src/appV2/redesign/components/SettingRow";
import { useEffect, useState } from "react";

import { Text } from "../../../components/Text";
import { TextInput } from "../../../components/TextInput";

interface AutoVerifyThresholdSettingRowProps {
  thresholdInMinutes: number;
  disabled?: boolean;
  onChange: (minutes: number) => void;
}

const MAX_MINUTES = 30;
const FALLBACK_MINUTES_WHEN_ENABLED = 5;

export function AutoVerifyThresholdSettingRow({
  thresholdInMinutes,
  disabled = false,
  onChange,
}: AutoVerifyThresholdSettingRowProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const isEnabled = thresholdInMinutes > 0;
  const [draftInput, setDraftInput] = useState(thresholdInMinutes.toString());
  const [lastEnabledMinutes, setLastEnabledMinutes] = useState(
    thresholdInMinutes > 0 ? thresholdInMinutes : FALLBACK_MINUTES_WHEN_ENABLED
  );

  // Sync the input draft when the persisted value changes (after a successful
  // save or external refetch). Also captures the last non-zero value so
  // toggling off → on restores the prior amount.
  useEffect(() => {
    setDraftInput(thresholdInMinutes.toString());
    if (thresholdInMinutes > 0) {
      setLastEnabledMinutes(thresholdInMinutes);
    }
  }, [thresholdInMinutes]);

  const parsedDraft = Number(draftInput);
  const displayedMinutes =
    draftInput !== "" && !Number.isNaN(parsedDraft)
      ? Math.max(0, Math.min(MAX_MINUTES, Math.trunc(parsedDraft)))
      : thresholdInMinutes;

  // Switch is fully controlled by `isEnabled` (derived from prop). We never
  // mutate local state eagerly here — if the parent's onChange is wrapped in a
  // guard that blocks (e.g. unsigned-contract gate), the prop stays put and
  // the Switch correctly stays in its current visual state.
  const handleToggle = (checked: boolean) => {
    onChange(checked ? lastEnabledMinutes : 0);
  };

  const handleInputCommit = () => {
    if (!isEnabled) {
      return;
    }
    const parsed = Number(draftInput);
    if (draftInput === "" || Number.isNaN(parsed)) {
      setDraftInput(thresholdInMinutes.toString());
      return;
    }
    const clamped = Math.max(0, Math.min(MAX_MINUTES, Math.trunc(parsed)));
    if (clamped !== thresholdInMinutes) {
      onChange(clamped);
    } else {
      setDraftInput(thresholdInMinutes.toString());
    }
  };

  return (
    <SettingRow
      title={tu("Auto-verify shifts after clock-out (Grace Period)")}
      subtitle={
        isEnabled
          ? tu(
              "Shifts will be automatically verified for clocking out up to {{minutes}} {{unit}} over their scheduled shift end time. Only applied when shift extension is enabled for this workplace.",
              {
                values: {
                  minutes: displayedMinutes,
                  unit: displayedMinutes === 1 ? tu("minute") : tu("minutes"),
                },
              }
            )
          : tu(
              "Toggle on to automatically verify shifts that clock out shortly after their scheduled end time. Only applied when shift extension is enabled for this workplace."
            )
      }
      disabled={disabled}
      action={
        <Stack direction="row" alignItems="center" spacing={3}>
          <Switch
            checked={isEnabled}
            disabled={disabled}
            inputProps={{ "aria-label": tu("Enable auto-verify shifts after clock-out") }}
            onChange={(event) => {
              handleToggle(event.target.checked);
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextInput
              type="number"
              size="small"
              disabled={disabled || !isEnabled}
              value={draftInput}
              inputProps={{
                "aria-label": tu("Auto-verify threshold in minutes"),
                min: 0,
                max: MAX_MINUTES,
              }}
              sx={{ width: 64 }}
              onChange={(event) => {
                setDraftInput(event.target.value);
              }}
              onBlur={handleInputCommit}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleInputCommit();
                }
              }}
            />
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {tu("min")}
            </Text>
          </Stack>
        </Stack>
      }
    />
  );
}
